interface CacheEntry<T> {
    data: T;
    timestamp: number;
    ttl: number;
    key: string;
    hits: number;
}

interface CacheConfig {
    maxSize: number;
    defaultTTL: number;
    cleanupInterval: number;
}

class SmartCache {
    private cache = new Map<string, CacheEntry<any>>();
    private config: CacheConfig;
    private cleanupTimer: NodeJS.Timeout | null = null;

    constructor(config: Partial<CacheConfig> = {}) {
        this.config = {
            maxSize: 100,
            defaultTTL: 300000, // 5 minutes
            cleanupInterval: 60000, // 1 minute
            ...config
        };
        
        this.startCleanupTimer();
    }

    set<T>(key: string, data: T, ttl?: number): void {
        // Remove oldest entries if cache is full
        if (this.cache.size >= this.config.maxSize) {
            this.evictOldest();
        }

        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            ttl: ttl || this.config.defaultTTL,
            key,
            hits: 0
        });
    }

    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        
        if (!entry) {
            return null;
        }

        // Check if entry has expired
        if (Date.now() - entry.timestamp > entry.ttl) {
            this.cache.delete(key);
            return null;
        }

        // Increment hit counter for LRU
        entry.hits++;
        return entry.data;
    }

    has(key: string): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;
        
        if (Date.now() - entry.timestamp > entry.ttl) {
            this.cache.delete(key);
            return false;
        }
        
        return true;
    }

    delete(key: string): boolean {
        return this.cache.delete(key);
    }

    clear(): void {
        this.cache.clear();
    }

    size(): number {
        return this.cache.size;
    }

    getStats(): {
        size: number;
        hitRate: number;
        mostUsed: string[];
    } {
        const entries = Array.from(this.cache.values());
        const totalHits = entries.reduce((sum, entry) => sum + entry.hits, 0);
        const totalRequests = entries.length + totalHits;
        
        return {
            size: this.cache.size,
            hitRate: totalRequests > 0 ? totalHits / totalRequests : 0,
            mostUsed: entries
                .sort((a, b) => b.hits - a.hits)
                .slice(0, 5)
                .map(entry => entry.key)
        };
    }

    private evictOldest(): void {
        const entries = Array.from(this.cache.entries());
        const oldest = entries.reduce((oldest, [key, entry]) => {
            return entry.timestamp < oldest[1].timestamp ? [key, entry] : oldest;
        });
        
        this.cache.delete(oldest[0]);
    }

    private startCleanupTimer(): void {
        this.cleanupTimer = setInterval(() => {
            this.cleanup();
        }, this.config.cleanupInterval);
    }

    private cleanup(): void {
        const now = Date.now();
        const expiredKeys: string[] = [];
        
        for (const [key, entry] of Array.from(this.cache.entries())) {
            if (now - entry.timestamp > entry.ttl) {
                expiredKeys.push(key);
            }
        }
        
        expiredKeys.forEach(key => this.cache.delete(key));
        
        if (expiredKeys.length > 0) {
            console.log(`🧹 Cache cleanup: removed ${expiredKeys.length} expired entries`);
        }
    }

    destroy(): void {
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = null;
        }
        this.cache.clear();
    }
}

// Global cache instance
export const globalCache = new SmartCache({
    maxSize: 50,
    defaultTTL: 300000, // 5 minutes
    cleanupInterval: 60000 // 1 minute
});

// Cache key generators
export const generateCacheKey = (prefix: string, params: Record<string, any>): string => {
    const sortedParams = Object.keys(params)
        .sort()
        .map(key => `${key}:${params[key]}`)
        .join('|');
    return `${prefix}:${sortedParams}`;
};

// Cache decorator for functions
export const withCache = <T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    keyGenerator: (...args: T) => string,
    ttl?: number
) => {
    return async (...args: T): Promise<R> => {
        const key = keyGenerator(...args);
        
        // Check cache first
        const cached = globalCache.get<R>(key);
        if (cached !== null) {
            console.log(`📦 Cache hit for key: ${key}`);
            return cached;
        }
        
        // Execute function and cache result
        console.log(`🔄 Cache miss for key: ${key}, executing function`);
        const result = await fn(...args);
        globalCache.set(key, result, ttl);
        
        return result;
    };
};

export default SmartCache;
